(function () {
  'use strict';
  var shared = window.ErsProofStorage;
  if (!shared) return;
  var database;
  var TYPES = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp', 'image/svg+xml': 'svg', 'application/pdf': 'pdf', 'application/illustrator': 'ai', 'application/postscript': 'eps' };
  function clone(value) { return JSON.parse(JSON.stringify(value)); }
  function checksum(bytes) {
    return crypto.subtle.digest('SHA-256', bytes).then(function (digest) {
      return Array.from(new Uint8Array(digest), function (byte) { return byte.toString(16).padStart(2, '0'); }).join('');
    });
  }
  function dataBlob(value) {
    var match = /^data:([^;,]+);base64,([\s\S]+)$/.exec(value || '');
    if (!match) throw new Error('A required artwork source could not be read. Please upload it again.');
    return new Blob([Uint8Array.from(atob(match[2]), function (c) { return c.charCodeAt(0); })], { type: match[1] });
  }
  function openDatabase() {
    if (!database) database = new Promise(function (resolve, reject) {
      // Retain existing Classic attempts; both UIs now use this exact cache.
      var request = indexedDB.open('ers-chopstick-product-proof', 1);
      request.onupgradeneeded = function () { request.result.createObjectStore('attempts', { keyPath: 'reference' }); };
      request.onsuccess = function () { resolve(request.result); };
      request.onerror = function () { reject(request.error); };
      request.onblocked = function () { reject(new Error('Proof storage is blocked. Close other designer tabs and retry.')); };
    });
    return database;
  }
  async function cache(key, attempt) {
    var db = await openDatabase();
    var pointer = sessionStorage.getItem(key) || '';
    if (!attempt && pointer.charAt(0) === '{') {
      // Old Guided snapshots contain no source bytes. Do not silently create a
      // second order or downgrade a pending request to bypass source checks.
      var legacy = JSON.parse(pointer);
      if (legacy && !legacy.verified && legacy.reference) throw new Error('An earlier proof request is still pending (' + legacy.reference + '). Keep this reference and contact ERS before submitting it again.');
      return null;
    }
    return new Promise(function (resolve, reject) {
      var transaction = db.transaction('attempts', attempt ? 'readwrite' : 'readonly');
      var store = transaction.objectStore('attempts');
      var request = attempt ? store.put(attempt) : store.get(pointer);
      transaction.oncomplete = function () {
        try { if (attempt) sessionStorage.setItem(key, attempt.reference); resolve(attempt || request.result || null); }
        catch (error) { reject(error); }
      };
      transaction.onerror = transaction.onabort = function () { reject(transaction.error || new Error('This browser could not preserve the proof attempt.')); };
    });
  }
  async function normalize(input) {
    var design = clone(input.designDocument);
    if (!design || !design.sides) throw new Error('Your design is not ready. Please try again.');
    var files = [], descriptors = [], stock = [];
    async function upload(asset, kind, side) {
      if (!asset) throw new Error('A required artwork source is missing. Please upload it again.');
      var blob = asset.blob || (asset.dataUrl && dataBlob(asset.dataUrl));
      if (!(blob instanceof Blob) || !blob.size || !TYPES[blob.type]) throw new Error('A required artwork source is missing or unsupported. Please upload it again.');
      var hash = await checksum(await blob.arrayBuffer());
      var id = 'source-' + hash;
      if (!descriptors.some(function (source) { return source.id === id; })) {
        var file = new File([blob], id + '.' + TYPES[blob.type], { type: blob.type });
        files.push({ id: id, file: file });
        descriptors.push({ id: id, filename: file.name, originalName: asset.name || 'Artwork', kind: kind, side: side || null, contentType: file.type, size: file.size, sha256: hash });
      }
      return id;
    }
    for (var side of ['front', 'back']) {
      for (var object of design.sides[side].objects) {
        if (object.type !== 'image') continue;
        var ref = object.assetRef;
        if (ref && ref.kind === 'template') {
          var url = ref.url;
          if (!url) throw new Error('A stock artwork source is unavailable. Please choose the sample again.');
          var resolved = new URL(url, location.href);
          if (resolved.protocol !== 'https:' && resolved.origin !== location.origin) throw new Error('The stock artwork URL is invalid.');
          ref.url = resolved.href;
          stock.push({ kind: 'stock', side: side, objectId: object.id, sourceId: ref.sourceId, url: ref.url, crop: ref.crop });
        } else if (ref && ref.kind === 'design') {
          object.assetRef = { kind: 'design', assetId: await upload(input.assets[ref.assetId], 'customer-upload', side) };
        } else throw new Error('A required artwork source is unresolved. Please upload it again.');
      }
    }
    for (var attachment of input.attachments || []) await upload(attachment, 'customer-attachment');
    if (files.length > 32 || files.reduce(function (n, source) { return n + source.file.size; }, 0) > 6 * 1024 * 1024) throw new Error('Source files must total 6 MB or less. Your design is preserved; please use smaller artwork files.');
    files.sort(function (a, b) { return a.id.localeCompare(b.id); });
    descriptors.sort(function (a, b) { return a.id.localeCompare(b.id); });
    var contact = Object.assign({}, input.contact, { email: input.contact.email.trim().toLowerCase(), sourceFiles: descriptors, stockArtwork: stock });
    return { designDocument: design, order: clone(input.order), contact: contact, files: files,
      fingerprint: await checksum(new TextEncoder().encode(JSON.stringify({ design: design, order: input.order, contact: contact }))) };
  }
  function canvasFile(canvas, name) {
    return new Promise(function (resolve, reject) {
      if (!canvas) { reject(new Error('Your latest design preview is still preparing. Please wait and retry.')); return; }
      canvas.toBlob(function (blob) {
        if (!blob || blob.type !== 'image/webp') reject(new Error('The design preview could not be prepared.'));
        else resolve(new File([blob], name, { type: 'image/webp' }));
      }, 'image/webp', .96);
    });
  }
  function money(value) { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0) / 100); }
  function contactValues(attempt) {
    var receipt = attempt.receipt, metadata = JSON.parse(attempt.metadata), order = metadata.order, contact = metadata.productPage || {};
    var lines = [
      'Request type: Custom sleeve proof request — no payment collected', 'Reference: ' + receipt.reference,
      'Customer email: ' + metadata.email, 'Product: ' + order.productTitle, 'Product URL: ' + order.productUrl,
      'Product ID: ' + order.productId, 'Chopsticks: ' + order.variantTitle + ' — ' + order.variantDescriptor,
      'Variant ID: ' + order.variantId, 'SKU: ' + order.sku, 'Quantity: ' + order.quantity + ' cases',
      'Total pairs: ' + order.totalUnits, 'Print colors selected: ' + order.printColors,
      'Artwork colors detected: ' + order.detectedPrintColors, 'Merchandise: ' + money(order.merchandiseTotal),
      'Setup fee: ' + money(order.setupFee), 'Estimated merchandise + setup subtotal: ' + money(order.estimatedTotal) + ' (freight and tax additional)',
      'Artwork source: ' + (contact.artworkSourceLabel || 'Designer'),
      'Artwork: ' + attempt.artworkFile.name + ' (combined Front + Back WebP)',
      'Durable artwork link: ' + receipt.artworkUrl, 'Submission details and source files: ' + receipt.submissionUrl,
      'Storage submission ID: ' + receipt.submissionId, 'Country: ' + (contact.country || ''), 'Notes: ' + (contact.notes || '')
    ];
    (contact.sourceFiles || []).forEach(function (source) { lines.push(source.kind + ': ' + source.originalName + ' — source ID ' + source.id); });
    (contact.stockArtwork || []).forEach(function (source) { lines.push(source.side + ' stock artwork: ' + source.sourceId + ' — ' + source.url); });
    ['front', 'back'].forEach(function (side) { lines.push(side + ': ' + metadata.designDocument.sides[side].objects.length + ' design objects'); });
    return { product: order.productTitle, productUrl: order.productUrl, productId: order.productId, variantId: order.variantId,
      sku: order.sku, chopstick: order.variantTitle + ' — ' + order.variantDescriptor, quantity: order.quantity, totalUnits: order.totalUnits,
      printColors: order.printColors, setupFee: money(order.setupFee), estimatedTotal: money(order.estimatedTotal), reference: receipt.reference,
      artworkUrl: receipt.artworkUrl, submissionUrl: receipt.submissionUrl, storageSubmissionId: receipt.submissionId,
      artworkFilename: attempt.artworkFile.name, summary: lines.join('\n') };
  }
  var FIELD_NAMES = { product: 'Product', productUrl: 'Product URL', productId: 'Product ID', variantId: 'Variant ID', sku: 'SKU',
    chopstick: 'Chopsticks', quantity: 'Quantity (cases)', totalUnits: 'Total units', printColors: 'Print colors', setupFee: 'Setup fee',
    estimatedTotal: 'Estimated total', reference: 'Proof reference', artworkUrl: 'Artwork link', submissionUrl: 'Submission details',
    storageSubmissionId: 'Storage submission ID', artworkFilename: 'Artwork filename', summary: 'body' };
  function attach(options) {
    var root = options.root, form = options.form, button = options.button, label = options.label || button;
    var storage = shared.create(options.endpoint), pending = null, busy = false, nativeDispatch = false, notified = false, initialized = false, destroyed = false, initializationError = null;
    var locked = [];
    function unlock() { root.inert = false; locked.forEach(function (entry) { entry.element.inert = entry.inert; }); locked = []; }
    function error(message) {
      busy = false; nativeDispatch = false; notified = false; unlock();
      form.dataset.proofReceiptReady = form.dataset.csdReceiptReady = 'false';
      button.disabled = false; label.textContent = options.submitLabel;
      options.onError(message);
    }
    function validReceipt(attempt) {
      if (!attempt || !attempt.receipt) return false;
      try { shared.verifyReceipt(attempt, attempt.receipt, options.endpoint); return true; } catch (failure) { return false; }
    }
    function populate() {
      var values = contactValues(pending);
      Object.keys(values).forEach(function (key) {
        var selector = options.fields && options.fields[key];
        var field = selector && form.querySelector(selector);
        if (!field) field = form.querySelector('[name="contact[' + FIELD_NAMES[key] + ']"]');
        if (!field) { field = document.createElement('input'); field.type = 'hidden'; field.name = 'contact[' + FIELD_NAMES[key] + ']'; form.append(field); }
        field.value = values[key] == null ? '' : String(values[key]);
      });
      options.email.value = pending.email;
      // Sources go only to R2; the notification gets canonical protected links.
      form.querySelectorAll('input[type="file"]').forEach(function (input) { input.disabled = true; });
    }
    function success() { unlock(); busy = false; button.disabled = true; options.onSuccess(pending); }
    async function handleSubmit(event) {
      if (nativeDispatch && validReceipt(pending)) { notified = true; populate(); return; }
      event.preventDefault(); event.stopImmediatePropagation();
      if (!initialized || busy || notified || !form.reportValidity()) return;
      if (initializationError) { error(initializationError.message); return; }
      busy = true; button.disabled = true; label.textContent = 'Submitting…'; options.onError('');
      root.inert = true;
      locked = Array.from(root.querySelectorAll('input,textarea,select,button'), function (element) { var entry = { element: element, inert: element.inert }; element.inert = true; return entry; });
      try {
        await shared.protectContact(form, options.email, function () { return destroyed; });
        var input = await options.getContext();
        var context = await normalize(input);
        if (!pending || pending.fingerprint !== context.fingerprint) {
          var reference = shared.createReference();
          if (options.onReference) options.onReference(reference);
          var order = context.order;
          var snapshot = { reference: reference, productTitle: order.variantTitle, productDescriptor: order.variantDescriptor,
            variantId: order.variantId, quantity: order.quantity, totalUnits: order.totalUnits, merchandiseTotal: order.merchandiseTotal,
            setupFee: order.setupFee, estimatedTotal: order.estimatedTotal, printColors: order.printColors, preparedAt: new Date().toISOString() };
          pending = { version: 2, reference: reference, attemptId: reference, fingerprint: context.fingerprint, email: context.contact.email,
            metadata: shared.metadata(reference, context.contact.email, context.order, context.designDocument, snapshot, context.contact),
            artworkFile: await canvasFile(shared.buildProofCanvas(input.artwork, reference), reference + '-front-back.webp'),
            sourceFiles: context.files, contact: context.contact, recovery: input.recovery, confirmation: snapshot, notificationState: 'pending' };
        }
        await cache(options.cacheKey, pending);
        if (pending.notificationState === 'complete' && validReceipt(pending)) { success(); return; }
        if (!validReceipt(pending)) { pending.receipt = await storage.submit(pending); shared.verifyReceipt(pending, pending.receipt, options.endpoint); await cache(options.cacheKey, pending); }
        if (destroyed) return;
        pending.notificationState = 'sending'; await cache(options.cacheKey, pending);
        populate(); root.inert = false;
        form.dataset.proofReceiptReady = form.dataset.csdReceiptReady = 'true';
        nativeDispatch = true;
        form.requestSubmit();
      } catch (failure) { if (!destroyed) error(failure.message || 'The proof request could not be sent. Your design is preserved; please retry.'); }
    }
    function edited() {
      if (!initialized || busy) return;
      nativeDispatch = false; notified = false; button.disabled = false; label.textContent = options.submitLabel;
      form.dataset.proofReceiptReady = form.dataset.csdReceiptReady = 'false';
    }
    function pageShow(event) { if (event.persisted && !options.succeeded) error('Your artwork is preserved. Retry to finish sending the request.'); }
    form.addEventListener('submit', handleSubmit, true);
    form.addEventListener('input', edited); form.addEventListener('change', edited); window.addEventListener('pageshow', pageShow);
    button.disabled = true;
    var ready = cache(options.cacheKey).then(async function (saved) {
      pending = saved;
      if (options.succeeded) {
        if (!validReceipt(pending) || ['sending', 'complete'].indexOf(pending.notificationState) === -1) throw new Error('Shopify accepted the notification, but the durable artwork receipt could not be verified. Keep this page open and contact ERS support.');
        pending.notificationState = 'complete'; await cache(options.cacheKey, pending); success();
      } else {
        if (pending && pending.notificationState !== 'complete' && options.restore) {
          await options.restore(pending.recovery || { design: pending.design, contact: pending.contact, order: JSON.parse(pending.metadata).order, sourceFiles: pending.sourceFiles });
          if (options.onReference) options.onReference(pending.reference);
          if (!pending.version) {
            // Existing immutable Classic multipart attempts retain their original
            // bytes/reference. Only the new shared fingerprint is attached.
            pending.fingerprint = (await normalize(await options.getContext())).fingerprint;
            pending.version = 2; await cache(options.cacheKey, pending);
          }
        }
        button.disabled = false;
      }
      initialized = true;
    }).catch(function (failure) { initialized = true; initializationError = failure; error(failure.message); });
    return { ready: ready, edited: edited,
      confirmation: function () { return pending && pending.notificationState === 'complete' && validReceipt(pending) ? Object.assign({}, pending.confirmation || JSON.parse(pending.metadata).submittedSnapshot, { verified: true }) : null; },
      destroy: function () { destroyed = true; storage.destroy(); unlock(); form.removeEventListener('submit', handleSubmit, true); form.removeEventListener('input', edited); form.removeEventListener('change', edited); window.removeEventListener('pageshow', pageShow); }
    };
  }
  window.ErsProofSubmission = { attach: attach, normalize: normalize, dataBlob: dataBlob };
}());
